import React, {useMemo} from 'react';
import {Box, Stack, SxProps} from '@mui/material';
import {designSystemToken, Icon, Label} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {ProjectStatus} from '../../types/project';

enum StatusStyle {
  Processing = 'processing',
  ProcessingOverDue = 'processingOverDue',
  Ready = 'ready',
  Failed = 'failed',
  Default = 'default',
}

const STATUS_STYLES = () => ({
  [StatusStyle.Processing]: {
    background: designSystemToken('semantic.bg.tertiary'),
    color: designSystemToken('semantic.fg.primary'),
    icon: (
      <Icon
        size="medium"
        appearance="neutral"
        name="Features-Duration"
        color={designSystemToken('semantic.fg.primary')}
      />
    ),
  },
  [StatusStyle.ProcessingOverDue]: {
    background: designSystemToken('semantic.bg.tertiary'),
    color: designSystemToken('semantic.fg.primary'),
    icon: null,
  },
  [StatusStyle.Ready]: {
    background: '#EEF3FE',
    color: designSystemToken('semantic.fg.success'),
    icon: (
      <Icon
        size="medium"
        appearance="neutral"
        name="Actions-Accept"
        color={designSystemToken('semantic.fg.success')}
      />
    ),
  },
  [StatusStyle.Failed]: {
    background: designSystemToken('semantic.bg.danger-secondary'),
    color: designSystemToken('semantic.fg.danger'),
    icon: <Icon size="medium" appearance="danger" name="Actions-Info-Line" />,
  },
  [StatusStyle.Default]: {
    background: designSystemToken('semantic.bg.tertiary'),
    color: designSystemToken('semantic.fg.primary'),
    icon: null,
  },
});

function ProjectProgressBadge({
  remainingMinutes,
  status,
  failedCount,
  showIcon = true,
  sx,
}: {
  remainingMinutes: number | null;
  status: ProjectStatus;
  failedCount?: number;
  showIcon?: boolean;
  sx?: SxProps;
}) {
  const statusStyleKey = useMemo(() => {
    if (status === ProjectStatus.FAILED) {
      return StatusStyle.Failed;
    }
    if (status === ProjectStatus.READY) {
      return StatusStyle.Ready;
    }
    if (status === ProjectStatus.IN_PROGRESS) {
      if (typeof remainingMinutes === 'number') {
        return StatusStyle.Processing;
      }
      return StatusStyle.ProcessingOverDue;
    }
    return undefined;
  }, [status, remainingMinutes]);

  const statusText = useMemo(() => {
    if (status === ProjectStatus.READY) {
      return translate('Ready');
    }
    if (status === ProjectStatus.IN_PROGRESS) {
      if (typeof remainingMinutes === 'number') {
        return translate('count min left', {count: remainingMinutes});
      }

      return translate('Processing');
    }
    if (status === ProjectStatus.FAILED) {
      return failedCount
        ? translate('failed count', {count: failedCount})
        : translate('Failed');
    }
    return 'Unknown status';
  }, [status, remainingMinutes, failedCount]);

  const statusStyle = useMemo(() => {
    const statusStyles = STATUS_STYLES();
    return statusStyleKey && statusStyles[statusStyleKey]
      ? statusStyles[statusStyleKey]
      : statusStyles[StatusStyle.Default];
  }, [statusStyleKey]);

  return (
    <Box sx={sx}>
      <Stack direction="row" alignItems="center">
        {statusStyle.icon && showIcon ? statusStyle.icon : null}
        <Label size="md" style={{color: statusStyle.color, padding: '3px 6px'}}>
          {statusText}
        </Label>
      </Stack>
    </Box>
  );
}

export default ProjectProgressBadge;
